import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  CheckCircle,
  Loader2,
  LockKeyhole,
  Mail,
  ShieldCheck,
  User,
} from 'lucide-react';
import { toast } from 'sonner';
import API_BASE from '../../utils/apiBase';
import { updateWorkforceAppMetadata, workforceAppFullName, workforceAppShortName } from '../../utils/workforceAppMetadata';
import WorkerInstallPrompt from '../../components/worker/WorkerInstallPrompt';
import { accessPrivacyCopy } from '../../lib/workforceAccess';

const API = API_BASE;

const safeColour = (value) => (/^#[0-9a-fA-F]{6}$/.test(value || '') ? value : '#0F766E');

export default function WorkerLoginPage() {
  const navigate = useNavigate();
  const { workspaceSlug } = useParams();
  const [searchParams] = useSearchParams();
  const requireSignIn = searchParams.get('sign_in') === '1';
  const storedSlug = localStorage.getItem('workerWorkspaceSlug') || '';
  const effectiveSlug = workspaceSlug || '';
  const [email, setEmail] = useState('');
  const [sendingMagicLink, setSendingMagicLink] = useState(false);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [branding, setBranding] = useState(null);
  const [loadingBranding, setLoadingBranding] = useState(Boolean(effectiveSlug));
  const [brandingError, setBrandingError] = useState('');
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (!effectiveSlug) {
      setLoadingBranding(false);
      return;
    }

    setLoadingBranding(true);
    setBrandingError('');
    axios.get(`${API}/public/workforce/${effectiveSlug}`)
      .then((res) => {
        setBranding(res.data);
        if (res.data?.workforce_slug) {
          const currentToken = localStorage.getItem('workerToken');
          const currentSlug = localStorage.getItem('workerWorkspaceSlug');
          if (currentToken && !currentSlug) {
            localStorage.removeItem('workerToken');
            localStorage.removeItem('workerEmployee');
          }
          if (!currentToken || (currentSlug && currentSlug === res.data.workforce_slug)) {
            localStorage.setItem('workerWorkspaceSlug', res.data.workforce_slug);
          }
        }
      })
      .catch(() => {
        setBrandingError('This workforce portal is unavailable. Please use the private link supplied by your care provider.');
      })
      .finally(() => setLoadingBranding(false));
  }, [effectiveSlug]);

  useEffect(() => {
    if (!branding?.workforce_slug) return;
    const canonicalSlug = branding.workforce_slug;
    const tenantName = branding.tenant_name || branding.brand_name || 'Your care provider';
    updateWorkforceAppMetadata({
      tenantName,
      shortName: workforceAppShortName(tenantName),
      slug: canonicalSlug,
      primaryColour: branding.primary_colour,
    });

    const workerToken = localStorage.getItem('workerToken');
    const storedWorkspaceSlug = localStorage.getItem('workerWorkspaceSlug');
    if (!requireSignIn && workerToken && storedWorkspaceSlug === canonicalSlug) {
      navigate('/worker/dashboard');
    }
  }, [branding, navigate, requireSignIn]);

  const returnToStoredTenant = () => {
    if (storedSlug) navigate(`/workforce/${storedSlug}`);
  };

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((value) => (value > 0 ? value - 1 : 0)), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const sendMagicLink = async ({ isResend = false } = {}) => {
    const canonicalSlug = branding?.workforce_slug;
    if (!effectiveSlug || !canonicalSlug) {
      toast.error('Please use the private workforce link supplied by your care provider.');
      return;
    }
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    setSendingMagicLink(true);
    try {
      await axios.post(`${API}/worker/request-login`, {
        email,
        workspace_slug: canonicalSlug,
      });
      const currentToken = localStorage.getItem('workerToken');
      const currentSlug = localStorage.getItem('workerWorkspaceSlug');
      if (currentToken && currentSlug !== canonicalSlug) {
        localStorage.removeItem('workerToken');
        localStorage.removeItem('workerEmployee');
      }
      localStorage.setItem('workerWorkspaceSlug', canonicalSlug);
      setMagicLinkSent(true);
      setCooldown(30);
      toast.success(isResend
        ? 'A fresh secure link is on its way.'
        : 'Check your email for a secure access link.');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to send the secure access link.';
      toast.error(message);
    } finally {
      setSendingMagicLink(false);
    }
  };

  const handleMagicLinkRequest = (e) => {
    e.preventDefault();
    sendMagicLink({ isResend: false });
  };

  if (!effectiveSlug) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-2xl">
          <CardContent className="pt-8 pb-8 text-center space-y-4">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto">
              <User className="h-8 w-8 text-teal-700" />
            </div>
            <h1 className="text-2xl font-bold text-slate-800">Applicant & Workforce Portal</h1>
            <p className="text-sm text-slate-600">
              Please use the private workforce link supplied by your care provider.
            </p>
            {storedSlug && (
              <Button onClick={returnToStoredTenant} className="w-full bg-teal-700 hover:bg-teal-800">
                Open my care provider portal
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const primaryColour = safeColour(branding?.primary_colour);
  const tenantName = branding?.tenant_name || branding?.brand_name || 'Your care provider';
  const appName = workforceAppShortName(tenantName);
  const fullAppName = workforceAppFullName(tenantName);
  if (magicLinkSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full border-0 shadow-2xl overflow-hidden">
          <div className="h-1.5" style={{ backgroundColor: primaryColour }} />
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Check your email</h2>
            <p className="text-slate-600 mb-4">
              If <strong>{email}</strong> is linked to {tenantName}, a secure one-time link will arrive shortly.
            </p>
            <p className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600 mb-6">
              The link expires in 24 hours and can only be used once.
            </p>
            <Button
              onClick={() => sendMagicLink({ isResend: true })}
              disabled={sendingMagicLink || cooldown > 0}
              className="w-full mb-3 text-white"
              style={{ backgroundColor: primaryColour }}
              data-testid="worker-resend-link"
            >
              {cooldown > 0 ? `Send again in ${cooldown}s` : 'Send another secure link'}
            </Button>
            <Button variant="outline" onClick={() => setMagicLinkSent(false)} className="w-full">
              Try a different email
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_#effcf8_0,_#f8fafc_34%,_#e2e8f0_100%)] flex items-center justify-center p-4">
      <Card className="max-w-5xl w-full border border-white/70 shadow-2xl overflow-hidden bg-white/95 backdrop-blur">
        <div className="grid lg:grid-cols-[0.95fr_1.05fr]">
          <CardHeader className="relative text-left p-6 sm:p-8 lg:p-10 text-white overflow-hidden" style={{ backgroundColor: primaryColour }}>
            <div className="absolute inset-0 bg-[linear-gradient(135deg,_rgba(15,23,42,0.32),_rgba(15,23,42,0.04))]" />
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg overflow-hidden bg-white/95">
                  {branding?.logo_url ? (
                    <img src={branding.logo_url} alt={tenantName} className="max-h-11 max-w-12 object-contain" />
                  ) : (
                    <User className="h-7 w-7" style={{ color: primaryColour }} />
                  )}
                </div>
                <div>
                  <p className="text-xs uppercase tracking-[0.24em] text-white/75">Private portal</p>
                  <CardTitle className="text-2xl sm:text-3xl font-bold text-white">{tenantName}</CardTitle>
                </div>
              </div>

              <h1 className="text-3xl sm:text-4xl font-semibold leading-tight mb-4">{fullAppName}</h1>
              <p className="text-base text-white/85 leading-relaxed mb-8">
                A secure place to continue your application, onboarding and work tasks.
              </p>

              <div className="space-y-3">
                <div className="flex items-center gap-3 rounded-2xl bg-white/12 border border-white/20 px-4 py-3">
                  <ShieldCheck className="h-5 w-5 text-white" />
                  <span className="text-sm text-white/90">Tenant-branded access only</span>
                </div>
                <div className="flex items-center gap-3 rounded-2xl bg-white/12 border border-white/20 px-4 py-3">
                  <LockKeyhole className="h-5 w-5 text-white" />
                  <span className="text-sm text-white/90">One-time email links, no shared passwords</span>
                </div>
              </div>
            </div>
          </CardHeader>

          <CardContent className="p-6 sm:p-8 lg:p-10">
          {loadingBranding ? (
            <div className="py-16 text-center text-sm text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-3" />
              Preparing your secure portal...
            </div>
          ) : brandingError ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">{brandingError}</p>
          ) : (
            <form onSubmit={handleMagicLinkRequest} className="space-y-5">
              <div className="rounded-3xl border border-slate-200 bg-slate-50/80 p-5 space-y-4">
                <div>
                  <p className="text-sm font-medium text-slate-500">Secure access</p>
                  <h2 className="text-2xl font-bold text-slate-900 mt-1">Send me a secure link</h2>
                  <p className="text-sm text-slate-500 mt-1" data-testid="worker-access-helper">
                    Enter the email you used with {tenantName}. We&apos;ll send a one-time sign-in link and take you to the right application, onboarding or work area. No password required.
                  </p>
                  {requireSignIn && (
                    <p className="mt-3 text-sm font-medium text-teal-800" data-testid="upload-personal-sign-in">
                      Sign in with your own email to see your saved progress and any remaining steps.
                    </p>
                  )}
                </div>
              <div>
                <Label className="text-slate-700">Email address</Label>
                <Input
                  type="email"
                  placeholder="your.email@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12 text-base mt-2"
                  required
                  data-testid="worker-email-input"
                />
              </div>

              <Button
                type="submit"
                disabled={sendingMagicLink}
                className="w-full h-12 text-base gap-2 text-white"
                style={{ backgroundColor: primaryColour }}
                data-testid="worker-magic-link-submit"
              >
                {sendingMagicLink ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}
                {sendingMagicLink ? 'Sending...' : 'Send secure link'}
              </Button>
              </div>

              {branding?.workforce_slug && (
                <div
                  className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-center"
                  data-testid="worker-new-applicant-entry"
                >
                  <p className="text-xs text-slate-500">New applicant and haven&apos;t applied yet?</p>
                  <button
                    type="button"
                    onClick={() => navigate(`/apply/${branding.workforce_slug}`)}
                    className="mt-1 text-sm font-semibold text-teal-700 underline underline-offset-2 hover:text-teal-800"
                    data-testid="worker-apply-here-link"
                  >
                    Apply here
                  </button>
                </div>
              )}

              {(branding?.support_email || branding?.support_phone) && (
                <div className="rounded-2xl border border-teal-100 bg-teal-50 px-4 py-3 text-sm text-slate-700" data-testid="worker-support-contact">
                  <p className="font-semibold text-slate-900">Need help getting in?</p>
                  <p className="mt-1 text-xs text-slate-600">
                    Contact {tenantName} and tell us you need help accessing your portal.
                  </p>
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs font-medium">
                    {branding?.support_email ? (
                      <a className="text-teal-800 underline underline-offset-2" href={`mailto:${branding.support_email}`}>
                        {branding.support_email}
                      </a>
                    ) : null}
                    {branding?.support_phone ? (
                      <a className="text-teal-800 underline underline-offset-2" href={`tel:${String(branding.support_phone).replace(/\s+/g, '')}`}>
                        {branding.support_phone}
                      </a>
                    ) : null}
                  </div>
                </div>
              )}

              <p className="flex items-start gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-xs text-slate-500">
                <LockKeyhole className="h-4 w-4 shrink-0 text-slate-400 mt-0.5" />
                <span>
                  {accessPrivacyCopy({ tenantName })}
                </span>
              </p>
              {branding?.powered_by_careops && (
                <p className="text-[11px] text-center text-slate-400">Powered by CareOps</p>
              )}
              <WorkerInstallPrompt appName={appName} compact />
            </form>
          )}
        </CardContent>
        </div>
      </Card>
    </div>
  );
}
